import clsx from 'clsx';
import * as React from 'react';
import { useState } from 'react';

import { PhoneValidationContainer } from '@/components/Containers/PhoneValidationContainer/PhoneValidationContainer';
import { SpinnerContainer } from '@/components/Containers/SpinnerContainer';
import { ProductDetail } from '@/components/Elements';
import { iFormContactFields } from '@/components/Form/FormContact';
import { FormContactInModal } from '@/components/Form/FormContactInModal';
import { iFormPhoneValidationFields } from '@/components/Form/FormPhoneValidation';
import { iFormTokenCheckFields } from '@/components/Form/FormTokenCheck';
import { FormUserAccount, iFormUserAccountFields } from '@/components/Form/FormUserAccount';
import { ACTIVE, PENDING } from '@/config/appConstants';
import { Country } from '@/features/countries';
import { MobileToken } from '@/features/mobileToken';
import { Province } from '@/features/provinces';
import { TinDocumentType } from '@/features/tinDocumentTypes';
import { useGroupBillingDetails } from '@/hooks/useGroupBillingDetails';
import { useMe } from '@/hooks/useMe';
import { Currency, UserData, ProductTypes, UserProductTypes } from '@/types';
import {
  getOrigin,
  checkHasToShowPhoneValidationForm,
  filterUserProductsByStatus,
  getBillingModeFromUserProduct,
  isAppSumo,
} from '@/utils/helpers';

export type MyAccountPageContainerProps = {
  userData: UserData;
  disablePage?: boolean;
  countries: Country[];
  provinces: Province[];
  tinDocumentTypes: TinDocumentType[];
  countryOnChange: (formValues: any, countryId: string) => void;
  userProduct?: UserProductTypes | null;
  userProducts?: UserProductTypes[] | null;
  groupId: string;
  userCurrency: Currency | null;
  mobileToken: MobileToken;
  onSubmit: (values: iFormUserAccountFields, product: ProductTypes | null) => void;
  onSubmitFormPhoneValidation: (values: iFormPhoneValidationFields) => void;
  onSubmitFormTokenCheck: (values: iFormTokenCheckFields) => void;
  onCancelButton: (product: ProductTypes | null | undefined) => void;
  onRequestInfoSubmit: (values: iFormContactFields) => void;
};

export const MyAccountPageContainer = ({
  onRequestInfoSubmit,
  userData,
  countries,
  provinces,
  tinDocumentTypes,
  countryOnChange,
  userProduct,
  userProducts,
  groupId,
  userCurrency,
  mobileToken,
  onSubmit,
  onSubmitFormPhoneValidation,
  onSubmitFormTokenCheck,
  onCancelButton,
  disablePage,
}: MyAccountPageContainerProps) => {
  const userProductPending = filterUserProductsByStatus(userProducts || [], PENDING);
  const showCancelButton = !!userProductPending;
  const [requestInfoModalIsOpen, setRequestInfoModalIsOpen] = useState(false);
  const { getBillingDetails } = useGroupBillingDetails();
  const { getMe } = useMe();
  const billingDetails = getBillingDetails();

  const onDefaultRequestClose = () => {
    onCancelButton && onCancelButton(userProduct?.product);
  };

  const getContactForm = () => {
    const userProductActive = filterUserProductsByStatus(userProducts || [], ACTIVE);
    const { countryId, phone } = billingDetails;
    const { name, email } = userData;

    const onRequestInfoClose = () => setRequestInfoModalIsOpen(false);

    const onDefaultRequestInfoSubmit = (values: iFormContactFields) => {
      onRequestInfoSubmit(values);
      onRequestInfoClose();
    };

    return (
      <FormContactInModal
        isOpen={requestInfoModalIsOpen}
        shouldCloseOnOverlayClick={true}
        groupId={groupId}
        name={name}
        email={email}
        origin={getOrigin(
          userProducts && userProducts?.length > 0 ? userProducts[0].product : null
        )}
        product={getOrigin(userProductActive?.product)}
        countryId={countryId}
        phone={phone}
        onSubmit={onDefaultRequestInfoSubmit}
        onRequestClose={onRequestInfoClose}
      />
    );
  };

  const isBlocked: boolean = checkHasToShowPhoneValidationForm(mobileToken, getMe());

  const getSystemConfirmSuccess = () => {
    if (disablePage) {
      return (
        <div>
          <SpinnerContainer className="!min-h-[300px]" />
        </div>
      );
    }
  };

  return (
    <div className="mx-3 overflow mt-0 lg:mt-9">
      <div
        className={clsx('flex items-center items-stretch flex-wrap-reverse pb-20 relative', {
          page__disabled: disablePage,
        })}
      >
        <div className="p-1 w-full md:w-1/2 xl:w-3/4 pt-8">
          {billingDetails && getContactForm()}

          {isBlocked && (
            <PhoneValidationContainer
              mobileToken={mobileToken}
              userData={userData}
              onRequestInfo={() => setRequestInfoModalIsOpen(true)}
              onSubmitFormPhoneValidation={onSubmitFormPhoneValidation}
              onSubmitFormTokenCheck={onSubmitFormTokenCheck}
              showFooter={true}
            />
          )}
          {getSystemConfirmSuccess()}
          {!isBlocked && !disablePage && (
            <FormUserAccount
              onSubmit={onSubmit}
              countries={countries}
              provinces={provinces}
              tinDocumentTypes={tinDocumentTypes}
              countryOnChange={countryOnChange}
              groupId={groupId}
              showCancelButton={showCancelButton}
              showAgreementsCheck={isAppSumo(getMe())}
              onCancelButton={onDefaultRequestClose}
              currency={userCurrency}
              billingMode={null}
              onRequestInfo={() => setRequestInfoModalIsOpen(true)}
            />
          )}
        </div>
        <div className="p-1 w-full md:w-1/2 xl:w-1/4 pt-0 lg:pt-8">
          <ProductDetail
            userProducts={userProducts}
            product={userProduct?.product}
            userCurrency={userCurrency}
            billingMode={getBillingModeFromUserProduct(userProduct)}
            urlMoreInformation={'/account/feature-comparator'}
          />
        </div>
      </div>
    </div>
  );
};
